<script>
  import FollowUp from './FollowUp.svelte'
  import { QUESTION_TYPES, labelExamples } from './survey_builder.svelte.js'

  // One question: summarised until it's selected, then open for editing (its prompt, type, the type's options,
  // whether it's required, and the follow-up condition that decides when it's asked).
  let { builder, question, index, dragging, onDragStart, onDragEnd } = $props()

  // A summarised card drags from anywhere; the open one only from its handle, so its fields stay selectable.
  let handleHeld = $state(false)

  const selected = $derived(builder.selectedUid === question.uid)
  const draggable = $derived(!selected || handleHeld)
  const typeLabel = $derived(QUESTION_TYPES.find(type => type.name === question.type)?.label)
  const examples = $derived(labelExamples(question.type))
  const problem = $derived(builder.conditionProblem(question))
  const followUpSummary = $derived.by(() => {
    const source = builder.source(question)
    if (!source || problem) return null
    const { operator, value } = question.condition
    const choice = builder.answerChoices(source).filter(answer => [value].flat().includes(answer.value))
    return `Only if "${builder.prompt(source)}" ${builder.operatorLabel(source, operator)} ${choice.map(answer => answer.label).join(', ')}`
  })
  const sources = $derived(builder.sourcesFor(question))

  function setFollowUp(on) {
    question.condition = on ? builder.defaultCondition(sources[sources.length - 1]) : null
  }

  function setKey(value) {
    question.key = value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
    question.keyTyped = question.key !== ''
  }

  function startDrag(event) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(question.uid))
    onDragStart()
  }
</script>

{#snippet handle()}
  <span
    class="cursor-grab select-none px-1 text-gray-300 hover:text-gray-500"
    title="Drag to reorder"
    aria-hidden="true"
    onpointerdown={() => (handleHeld = true)}
    onpointerup={() => (handleHeld = false)}
  >
    <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <circle cx="7" cy="5" r="1.5" /><circle cx="13" cy="5" r="1.5" /><circle cx="7" cy="10" r="1.5" />
      <circle cx="13" cy="10" r="1.5" /><circle cx="7" cy="15" r="1.5" /><circle cx="13" cy="15" r="1.5" />
    </svg>
  </span>
{/snippet}

<div
  class="rounded-xl border bg-white transition-shadow {selected ? 'border-blue-400 shadow-sm' : 'border-gray-200'}
         {dragging ? 'opacity-50' : ''}"
  {draggable}
  ondragstart={startDrag}
  ondragend={() => {
    handleHeld = false
    onDragEnd()
  }}
  data-question-card
>
  {#if !selected}
    <div class="flex cursor-grab select-none items-start gap-2 p-3 active:cursor-grabbing">
      {@render handle()}
      <button type="button" class="min-w-0 flex-1 text-left" onclick={() => (builder.selectedUid = question.uid)}>
        <span class="block truncate text-sm font-semibold {question.prompt.trim() ? 'text-gray-900' : 'text-gray-400'}">
          {index + 1}. {builder.prompt(question)}
        </span>
        <span class="mt-1 flex flex-wrap items-center gap-1.5">
          <span class="status-badge status-badge-disabled">{typeLabel}</span>
          {#if question.required}<span class="status-badge status-badge-info">Required</span>{/if}
          {#if problem}
            <span class="status-badge status-badge-warning">Follow-up needs fixing</span>
          {:else if followUpSummary}
            <span class="truncate text-xs text-gray-500">{followUpSummary}</span>
          {/if}
        </span>
      </button>
    </div>
  {:else}
    <div class="field-box suite-form space-y-4 !rounded-xl">
      <div class="flex select-none items-center gap-2">
        {@render handle()}
        <span class="flex-1 text-sm font-semibold text-gray-900">Question {index + 1}</span>
        <button
          type="button"
          class="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
          title="Move up"
          disabled={index === 0}
          onclick={() => builder.move(question, index - 1)}
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <button
          type="button"
          class="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
          title="Move down"
          disabled={index === builder.questions.length - 1}
          onclick={() => builder.move(question, index + 1)}
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <button
          type="button"
          class="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
          title="Remove question"
          onclick={() => window.confirm(builder.removalWarning(question)) && builder.remove(question)}
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div>
        <label for="survey-question-{question.uid}-prompt">Question</label>
        <div class="input-wrapper">
          <input
            id="survey-question-{question.uid}-prompt"
            type="text"
            placeholder="e.g., Was your issue resolved?"
            bind:value={question.prompt}
          />
        </div>
      </div>

      <div>
        <span class="text-sm font-medium text-gray-900">Type</span>
        <div class="mt-1 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Type">
          {#each QUESTION_TYPES as type (type.name)}
            <button
              type="button"
              role="radio"
              aria-checked={question.type === type.name}
              class="rounded-full border px-3 py-1 text-sm font-medium transition-colors
                     {question.type === type.name
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'}"
              title={type.hint}
              onclick={() => builder.setType(question, type.name)}
            >
              {type.label}
            </button>
          {/each}
        </div>
      </div>

      {#if examples}
        <div class="grid grid-cols-2 gap-3">
          {#if question.type === 'scale'}
            <div>
              <label for="survey-question-{question.uid}-min">Lowest</label>
              <div class="input-wrapper">
                <input id="survey-question-{question.uid}-min" type="number" bind:value={question.min} />
              </div>
            </div>
            <div>
              <label for="survey-question-{question.uid}-max">Highest</label>
              <div class="input-wrapper">
                <input id="survey-question-{question.uid}-max" type="number" bind:value={question.max} />
              </div>
            </div>
          {/if}
          <div>
            <label for="survey-question-{question.uid}-min-label">Label for lowest</label>
            <div class="input-wrapper">
              <input
                id="survey-question-{question.uid}-min-label"
                type="text"
                placeholder="e.g., {examples[0]}"
                bind:value={question.minLabel}
              />
            </div>
          </div>
          <div>
            <label for="survey-question-{question.uid}-max-label">Label for highest</label>
            <div class="input-wrapper">
              <input
                id="survey-question-{question.uid}-max-label"
                type="text"
                placeholder="e.g., {examples[1]}"
                bind:value={question.maxLabel}
              />
            </div>
          </div>
        </div>
      {:else if question.type === 'choice'}
        <div class="space-y-2">
          <span class="text-sm font-medium text-gray-900">Choices</span>
          {#each question.values as _value, choiceIndex (choiceIndex)}
            <div class="flex items-center gap-2">
              <div class="input-wrapper !mt-0 flex-1">
                <input
                  type="text"
                  aria-label="Choice {choiceIndex + 1}"
                  placeholder="Choice {choiceIndex + 1}"
                  bind:value={question.values[choiceIndex]}
                />
              </div>
              <button
                type="button"
                class="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-30"
                title="Remove choice"
                disabled={question.values.length <= 2}
                onclick={() => question.values.splice(choiceIndex, 1)}
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}
          <button type="button" class="text-sm font-medium text-blue-600 hover:underline"
                  onclick={() => question.values.push('')}>
            Add choice
          </button>
          <div class="flex items-start justify-between gap-4 pt-2">
            <div class="flex-1">
              <span class="toggle-row-label">Allow more than one answer</span>
              <p class="toggle-row-description">Customers can pick several choices instead of one.</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" class="sr-only" aria-label="Allow more than one answer"
                     bind:checked={question.multiple} />
              <div class="toggle-switch-track"></div>
            </label>
          </div>
        </div>
      {/if}

      <div class="flex items-start justify-between gap-4 border-t border-gray-200 pt-3">
        <div class="flex-1">
          <span class="toggle-row-label">Required</span>
          <p class="toggle-row-description">Customers must answer this to finish the survey.</p>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" class="sr-only" aria-label="Required" bind:checked={question.required} />
          <div class="toggle-switch-track"></div>
        </label>
      </div>

      <div class="space-y-2 border-t border-gray-200 pt-3">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <span class="toggle-row-label">Follow-up</span>
            <p class="toggle-row-description">
              {#if sources.length}
                Ask this only when an earlier answer matches.
              {:else}
                To make this a follow-up, put a rating, scale, yes/no or multiple-choice question above it.
              {/if}
            </p>
          </div>
          {#if sources.length || question.condition}
            <label class="toggle-switch">
              <input
                type="checkbox"
                class="sr-only"
                aria-label="Follow-up"
                checked={Boolean(question.condition)}
                onchange={event => setFollowUp(event.currentTarget.checked)}
              />
              <div class="toggle-switch-track"></div>
            </label>
          {/if}
        </div>
        {#if question.condition}
          <FollowUp {builder} {question} {sources} />
          {#if problem}<p class="text-xs text-amber-700">{problem}</p>{/if}
        {/if}
      </div>

      <div class="border-t border-gray-200 pt-3">
        <label for="survey-question-{question.uid}-key">Reporting key</label>
        <div class="input-wrapper">
          <input
            id="survey-question-{question.uid}-key"
            type="text"
            class="font-mono"
            placeholder={builder.keys.get(question.uid)}
            value={question.keyTyped ? question.key : ''}
            oninput={event => setKey(event.currentTarget.value)}
          />
        </div>
        <p class="form-hint mt-1 text-xs text-gray-400">
          {#if question.id}
            Names this question in reports and exports. Changing it starts a new series in reports.
          {:else}
            Names this question in reports and exports. Leave blank to use
            <span class="font-mono">{builder.keys.get(question.uid)}</span>.
          {/if}
        </p>
      </div>
    </div>
  {/if}
</div>
